import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ChartProvider from '../../../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../../../charts/echarts/ColorProvider'
import Tooltip from '../../../../../charts/echarts/Tooltip'
import ComponentTreeMap from '../../../../../charts/echarts/ComponentTreeMap'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { buildSliceTooltipFormatter } from './allocationTooltip'

// Treemap variant of the allocation chart. Each slice becomes a tile;
// tile area is proportional to `abs` (negatives sized by absolute
// value, signed value still shown in the tooltip / detail table).
//
// Intentional non-interactivity (per allocation tool v1 design):
//
//   - roam: false       -- no pan or zoom on the tile rect.
//   - nodeClick: false  -- clicking a tile does nothing (no
//                          zoom-to-node behaviour).
//   - breadcrumb hidden -- no drill-down trail to render, since we
//                          never drill in v1.
//   - leafDepth left undefined -- explicit drill-down disabled.
//
// Future enhancement (out of scope today): clicking a tile could fire
// `openTool('allocation', { scope: 'row', row: ... })` for that
// item's children -- the row dispatch path already exists. If we
// enable that, keep ECharts' built-in zoom OFF (it's confusing for
// the use case) and route the click out instead.

export default function AllocationTreeMap ({ slices, valueFormat, height = 340 }) {
  const { formatter: fmt } = useFormattingContext()
  const tooltipFormatter = useMemo(
    () => buildSliceTooltipFormatter(fmt, valueFormat),
    [fmt, valueFormat]
  )

  // Flat single-level data. Each item carries raw / percent so the
  // shared tooltip formatter can render the same content as the
  // donut and bar variants. `id` keeps animation identity stable
  // across re-renders even for two rows that share a display `name`.
  const data = useMemo(() => slices.map(s => ({
    id: s.groupId != null ? String(s.groupId) : s.name,
    name: s.name,
    value: s.abs,
    raw: s.raw,
    percent: s.percent
  })), [slices])

  return (
    <ChartColorProvider theme='default'>
      <ChartProvider height={height} loadingTemplate='pie'>
        <Tooltip trigger='item' formatter={tooltipFormatter} />
        <ComponentTreeMap
          seriesId='allocation'
          data={data}
          roam={false}
          nodeClick={false}
          breadcrumb={{ show: false }}
          label={{ show: true, formatter: '{b}', color: '#fff' }}
          upperLabel={{ show: false }}
          itemStyle={{ gapWidth: 1, borderWidth: 0 }}
          left={0}
          top={0}
          right={0}
          bottom={0}
          // Disable hover effects entirely -- ECharts' default treemap
          // emphasis dims siblings and brightens the hovered tile,
          // which contradicts the v1 non-interactive design above
          // (`roam: false`, `nodeClick: false`, breadcrumb hidden).
          // `emphasis.disabled: true` turns the whole emphasis state
          // off, so hovering a tile is a visual no-op.
          emphasis={{ disabled: true }}
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

AllocationTreeMap.propTypes = {
  slices: PropTypes.arrayOf(PropTypes.shape({
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    raw: PropTypes.number.isRequired,
    abs: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  })).isRequired,
  valueFormat: PropTypes.string,
  height: PropTypes.number
}
